import { type FlatSurface, flatSurfaceSchema, type SizeMm } from '@repo/api-contracts';
import { type KeyboardEvent, type ReactElement, useState } from 'react';
import {
  fitOutlineToRect,
  type PhotoDimensions,
  rectPoints,
  surfaceScale,
} from '@/entities/edit-area';
import { SurfaceAdvancedDetails } from './surface-advanced-details';
import { SurfaceNumberField } from './surface-number-field';

interface SurfaceInspectorProps {
  readonly surface?: FlatSurface;
  /** 상품 실측. 새 표면의 실측으로 복사한다 — 이 화면에서 실측을 입력받지 않는다(정책 §1 프레임). */
  readonly measurement: SizeMm | null;
  readonly photoSize?: PhotoDimensions;
  /** 적용되면 null, 거부되면 그 사유. 사유는 고친 칸 아래에 그대로 보여준다. */
  readonly onApply: (surface: FlatSurface) => string | null;
  readonly onDirtyChange: (dirty: boolean) => void;
  readonly disabled: boolean;
  /** '고급: 표면 수치 조정' 펼침 여부(표면 패널이 쥔다). 없으면 닫힌 채 시작한다. */
  readonly advancedOpen?: boolean;
  readonly onAdvancedToggle?: (open: boolean) => void;
}

const RANGE_FIELDS = [
  ['left', '표면 왼쪽 (%)'],
  ['top', '표면 위쪽 (%)'],
  ['right', '표면 오른쪽 (%)'],
  ['bottom', '표면 아래쪽 (%)'],
] as const;
type RangeField = (typeof RANGE_FIELDS)[number][0];

const RANGE_ERROR = '표면 범위는 사진 안의 직사각형이어야 합니다.';

/**
 * 가로·세로 축척(px/mm)이 이만큼(비율) 넘게 다르면 경고한다. 상품 실측의 가로·세로 비율이 사진 속 상품 비율과 달라
 * 틀을 실측 비율로 넓히지 못한 경우다(사진 끝에 닿음) — 인쇄 영역이 축마다 다르게 늘어나 보이므로 실측을 고쳐야 한다.
 */
const SCALE_MISMATCH_RATIO = 0.02;
const hasScaleMismatch = (scale: { x: number; y: number }): boolean =>
  Math.abs(scale.x - scale.y) / Math.max(scale.x, scale.y) > SCALE_MISMATCH_RATIO;

/** 저장된 % 의 처음 표시 — 소수 첫째 자리(4.0, 95.9). 고치지 않은 칸은 이 글자가 아니라 저장된 값을 쓴다. */
const shownPercent = (value: number | undefined): string =>
  value === undefined ? '' : value.toFixed(1);

type CommitSource = 'enter' | 'blur';

interface FieldError {
  readonly field: RangeField;
  readonly message: string;
}

const shownValues = (
  stored: Record<RangeField, number | undefined>,
): Record<RangeField, string> => ({
  left: shownPercent(stored.left),
  top: shownPercent(stored.top),
  right: shownPercent(stored.right),
  bottom: shownPercent(stored.bottom),
});

const storedOf = (surface: FlatSurface | undefined): Record<RangeField, number | undefined> => ({
  left: surface?.points[0].x,
  top: surface?.points[0].y,
  right: surface?.points[2].x,
  bottom: surface?.points[2].y,
});

const samePoints = (a: FlatSurface['points'], b: FlatSurface['points']): boolean =>
  a.every((point, index) => point.x === b[index]?.x && point.y === b[index]?.y);

/**
 * 사진 범위의 수치 미세 조정과 키보드만으로 표면을 만드는 대안. 기본 생성 수단은 '상품 추출하기'다.
 * 칸은 Enter 를 누르거나 칸을 떠나면 바로 적용하고(적용 버튼 없음), Esc 는 저장된 값으로 되돌린다. 거부되면 친 글자와
 * 입력 중 표시를 남기고 그 칸 아래에 사유를 보인다. 새 표면은 네 칸을 다 채울 때까지 칸을 떠나도 적용하지 않는다.
 */
export const SurfaceInspector = ({
  surface,
  measurement,
  photoSize,
  onApply,
  onDirtyChange,
  disabled,
  advancedOpen,
  onAdvancedToggle,
}: SurfaceInspectorProps): ReactElement => {
  const stored = storedOf(surface);
  const [values, setValues] = useState<Record<RangeField, string>>(() => shownValues(stored));
  // 운영자가 고친 칸. 고치지 않은 칸은 적용할 때 저장된 값을 그대로 쓴다(표시 반올림이 저장값을 바꾸지 않게).
  // 고친 칸이 있으면 아직 적용되지 않은 입력이 있다는 뜻이다.
  const [touched, setTouched] = useState<ReadonlySet<RangeField>>(new Set());
  const pending = touched.size > 0;
  const [error, setError] = useState<FieldError | null>(null);
  // 마지막으로 칸에 맞춘 저장 표면. 적용·사진 위 조작으로 표면이 바뀌면 폼을 다시 만들지 않고 칸만 맞춘다 —
  // 폼을 다시 만들면 Tab 으로 옮겨 간 칸의 포커스가 사라진다. 고치는 중인 칸은 친 글자를 남긴다.
  const [synced, setSynced] = useState(surface);
  if (synced !== surface) {
    setSynced(surface);
    const next = shownValues(stored);
    if (!pending) {
      setValues(next);
      setError(null);
    } else {
      setValues({
        left: touched.has('left') ? values.left : next.left,
        top: touched.has('top') ? values.top : next.top,
        right: touched.has('right') ? values.right : next.right,
        bottom: touched.has('bottom') ? values.bottom : next.bottom,
      });
    }
  }
  const appliedValue = (field: RangeField): number => {
    const original = stored[field];
    return touched.has(field) || original === undefined ? Number(values[field]) : original;
  };
  const realSize = surface?.realSize ?? measurement;
  const scale =
    surface !== undefined && photoSize !== undefined ? surfaceScale(surface, photoSize) : null;

  const settle = (): void => {
    setError(null);
    setTouched(new Set());
    onDirtyChange(false);
  };
  /** 지금 입력으로 적용한다. 저장된 범위와 같으면 적용하지 않고 입력 중 표시만 지운다. */
  const commit = (field: RangeField, source: CommitSource): void => {
    if (disabled) return;
    if (realSize === null) {
      setError({ field, message: '상품 실측을 먼저 등록해 주세요.' });
      return;
    }
    if (RANGE_FIELDS.some(([key]) => values[key] === '')) {
      // 새 표면은 네 칸을 차례로 채우는 중이라 칸을 떠날 때마다 사유를 띄우지 않는다.
      if (surface === undefined && source === 'blur') return;
      setError({ field, message: RANGE_ERROR });
      return;
    }
    const points = rectPoints(
      appliedValue('left'),
      appliedValue('top'),
      appliedValue('right'),
      appliedValue('bottom'),
    );
    const base = {
      id: surface?.id ?? crypto.randomUUID(),
      type: 'flat' as const,
      realSize,
      points,
    };
    const parsed = flatSurfaceSchema.safeParse(
      surface?.outline === undefined
        ? base
        : { ...base, outline: fitOutlineToRect(surface.outline, surface.points, points) },
    );
    if (!parsed.success) {
      setError({ field, message: RANGE_ERROR });
      return;
    }
    if (surface !== undefined && samePoints(parsed.data.points, surface.points)) {
      settle();
      return;
    }
    const rejection = onApply(parsed.data);
    if (rejection !== null) {
      setError({ field, message: rejection });
      return;
    }
    settle();
  };
  const revert = (): void => {
    setValues(shownValues(stored));
    settle();
  };
  const keyHandler =
    (field: RangeField) =>
    (event: KeyboardEvent<HTMLInputElement>): void => {
      if (event.key === 'Enter') {
        event.preventDefault();
        if (pending) commit(field, 'enter');
      } else if (event.key === 'Escape') {
        event.preventDefault();
        revert();
      }
    };

  return (
    <form aria-label="표면 입력" noValidate onSubmit={(event) => event.preventDefault()}>
      <fieldset disabled={disabled} className="space-y-3">
        <p className="text-sm">
          {realSize === null
            ? '상품 실측이 없습니다. 상품 수정 화면에서 실측 규격을 등록해 주세요.'
            : `표면 실측 ${realSize.widthMm} × ${realSize.heightMm} mm (상품 실측)`}
        </p>
        {realSize !== null && scale !== null && hasScaleMismatch(scale) ? (
          <p className="text-sm text-amber-700">
            {`상품 실측(${realSize.widthMm} × ${realSize.heightMm} mm)과 사진 속 상품 비율이 달라 인쇄 영역이 가로·세로로 늘어나 보입니다. 상품 수정 화면에서 실측을 확인해 주세요.`}
          </p>
        ) : null}
        <SurfaceAdvancedDetails open={advancedOpen} onToggle={onAdvancedToggle}>
          <div className="grid grid-cols-2 gap-2">
            {RANGE_FIELDS.map(([field, label]) => (
              <SurfaceNumberField
                key={field}
                label={label}
                value={values[field]}
                onChange={(value) => {
                  setValues({ ...values, [field]: value });
                  setTouched(new Set([...touched, field]));
                  onDirtyChange(true);
                }}
                onKeyDown={keyHandler(field)}
                onBlur={() => {
                  if (pending) commit(field, 'blur');
                }}
                alert={error?.field === field ? error.message : null}
              />
            ))}
          </div>
          {scale !== null ? (
            <p className="text-sm text-muted-foreground">
              축척 가로 {scale.x.toFixed(3)} · 세로 {scale.y.toFixed(3)} px/mm
            </p>
          ) : null}
        </SurfaceAdvancedDetails>
      </fieldset>
    </form>
  );
};
